import React, { useContext } from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import { useTranslation } from '../../i18n';
import { LangContext } from '../index';
import LaunchIcon from '@mui/icons-material/Launch';

interface Props {
  title: string;
}

// Déclaration simplifiée pour la version publique
// Nous n'utilisons pas réellement ce composant dans la version publique
declare namespace JSX {
  interface IntrinsicElements {
    'stripe-pricing-table': any;
  }
}

export const PricingSection: React.FC<Props> = ({ title }) => {
  const { lang } = useContext(LangContext);
  const { t } = useTranslation(lang);

  return (
    <Box
      id="pricing"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        bgcolor: '#f8f9fa',  // Fond gris très clair
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Typography
        variant="h2"
        component="h2"
        sx={{
          mb: 6,
          fontWeight: 'bold',
          textAlign: 'center',
          color: '#1a237e',
          textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
        }}
      >
        {title}
      </Typography>

      <Box sx={{ width: '100%', maxWidth: 1200, mb: 8, mx: 2.5 }}>
        <stripe-pricing-table 
          pricing-table-id="prctbl_1Qw1pMCyvjFozKkmomJk0tWH"
          publishable-key="pk_live_51NQ9DaCyvjFozKkm2bIfbijT4yF2xz0kCuB1fJ1p2xHwaDMWeVGJhuh3EOu9yKjzM8K9aN7ZOZP1W3jaLOhbc8Vg00TCDCy156"
          pricing-table-columns="4"
          pricing-table-columns-m="1">
        </stripe-pricing-table>
      </Box>

      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '90%',
          borderRadius: 4,
          background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
          position: 'relative',
          overflow: 'hidden',
          mb: lang === 'fr' ? 8 : 0,
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              textDecoration: 'line-through',
              color: '#666666',
              mb: 2,
              fontSize: '1.5rem',
              fontWeight: 500,
            }}
          >
            {t('landing.sections.pricing.regularPrice')}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              component="div"
              sx={{
                fontWeight: 'bold',
                color: 'primary.main',
              }}
            >
              {t('landing.sections.pricing.specialOffer')}
            </Typography>
            <Typography
              variant="h3"
              component="div"
              sx={{
                fontWeight: 'bold',
                color: 'primary.main',
                mb: 3,
              }}
            >
              {t('landing.sections.pricing.specialPrice')}
            </Typography>
            <Box
              component="a"
              href="https://pay.footballcoachs.com/b/7sI3d0aT73aG19K14h"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-block',
                backgroundColor: '#2196f3',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                transition: 'background-color 0.3s ease',
                '&:hover': {
                  backgroundColor: '#1976d2',
                },
              }}
            >
              {t('landing.sections.pricing.payButton')}
            </Box>
          </Box>
        </Box>

        {/* Effet décoratif */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            background: 'linear-gradient(45deg, #2196f3 30%, #21cbf3 90%)',
            borderRadius: '50%',
            opacity: 0.1,
          }}
        />
      </Paper>

      {lang === 'fr' && (
        <>
          <Typography
            variant="h4"
            component="h3"
            sx={{
              mt: 8,
              mb: 2,
              textAlign: 'center',
              color: '#1a237e',
            }}
          >
            TacticaFoot est inclus dans la suite Football Coachs
          </Typography>
          <Box sx={{ 
            width: '100%', 
            maxWidth: 800,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3
          }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              href="https://footballcoachs.com"
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<LaunchIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem',
                py: 1.5,
                px: 4
              }}
            >
              Football Coachs
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};
